import { memo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../../utils/theme';
import Document from '../../icon/document';
import { IconName } from '../../icon/icon';
import InlineTextButton from '../../inline-text-button/inline-text-button';
import Link from '../../link/link';
import { getHref } from '../../schema-form/utils';
import Spinner from '../../spinner/spinner';
import { Tooltip } from '../../tooltip/tooltip';
import Typography from '../../typography/typography';
import {
  ActionsContainer,
  DocumentContainer,
  DocumentDescription,
  TRUNCATED_LENGTH,
  TitleAndDescriptionContainer,
  TitleWrapper,
} from '../file-upload-lists.styles';

interface IButtonCopy {
  remove: string;
  edit: string;
}

export interface IFile {
  originalName: string;
  uuid: string;
  file: string;
  description?: string;
  maxTitleLength?: number;
  isPolicyDocument?: boolean;
}
export interface IFileUploadList {
  files: IFile[];
  onRemove?: (uuid: string) => void;
  onEdit?: (uuid: string) => void;
  loading?: boolean;
  buttonCopy?: IButtonCopy;
  disabled?: boolean;
  maxTitleLength?: number;
  showPolicyDocumentInfo?: boolean;
}

const FileUploadList = ({
  files,
  onRemove,
  onEdit,
  loading = false,
  disabled = false,
  maxTitleLength = TRUNCATED_LENGTH,
  buttonCopy = {
    remove: 'Delete',
    edit: 'Edit',
  },
  showPolicyDocumentInfo = false,
}: IFileUploadList) => {
  const theme = useTheme();

  return loading ? (
    <LoadingContainer>
      <StyledLoadingTypography variant={TypographyVariant.bodyMd1}>
        Loading...
      </StyledLoadingTypography>
      <Spinner data-testid="ben-loader" size="30px" />
    </LoadingContainer>
  ) : (
    <StyledDocumentList data-testid="file-upload-list">
      {files.map((file) => {
        const { uuid, description, isPolicyDocument } = file;
        const originalName =
          showPolicyDocumentInfo && isPolicyDocument
            ? '(Policy document) ' + file.originalName
            : file.originalName;
        return (
          <ListItem key={uuid}>
            <DocumentContainer>
              <Document />
            </DocumentContainer>
            <TitleAndDescriptionContainer>
              <TitleWrapper>
                {originalName.length > maxTitleLength ? (
                  <Tooltip tooltipText={originalName}>
                    <Link
                      href={getHref(file.file)}
                      color={theme.palette.primary.main}
                      label={
                        originalName.length > maxTitleLength
                          ? originalName.slice(0, maxTitleLength) + '...'
                          : originalName
                      }
                      isDownload
                    />
                  </Tooltip>
                ) : (
                  <Link
                    href={getHref(file.file)}
                    color={theme.palette.primary.main}
                    label={originalName}
                    isDownload
                  />
                )}
              </TitleWrapper>
              {description && (
                <DocumentDescription variant={TypographyVariant.bodyMd1}>
                  {description}
                </DocumentDescription>
              )}
            </TitleAndDescriptionContainer>
            <ActionsContainer>
              {typeof onEdit === 'function' && (
                <InlineTextButton
                  aria-label={`${buttonCopy.edit} ${originalName}`}
                  iconName={IconName.Edit}
                  onClick={() => onEdit(uuid)}
                  label={buttonCopy.edit}
                  isLeftIcon
                />
              )}
              {typeof onRemove === 'function' && (
                <InlineTextButton
                  aria-label={`${buttonCopy.remove} ${originalName}`}
                  iconName={IconName.Trash}
                  onClick={() => onRemove(uuid)}
                  label={buttonCopy.remove}
                  disabled={disabled}
                  isLeftIcon
                />
              )}
            </ActionsContainer>
          </ListItem>
        );
      })}
    </StyledDocumentList>
  );
};

export default memo(FileUploadList);

const StyledDocumentList = styled.li`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
    width: 100%;
    border-top: 1px solid ${theme.palette.neutral[50]};
    border-bottom: 1px solid ${theme.palette.neutral[50]};
    padding-top: ${theme.spacing(2)};
    padding-bottom: ${theme.spacing(2)};
  `}
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  height: 98px;
`;

const StyledLoadingTypography = styled(Typography)`
  ${({ theme }) => css`
    text-align: center;
    margin-bottom: ${theme.spacing(1)};
  `}
`;
